import { getNextItemCode } from "../utils/subcontract-items/nextItemCode";
import { getDraftParentError } from "../utils/subcontract-items/draftItemParentRules";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppDialog } from "components/common/dialogs";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { Add } from "@mui/icons-material";
import {
  Alert,
  Box,
  Typography,
} from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import EntityFormDialog from "../components/EntityFormDialog";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppTextField } from "components/common/inputs";
import {
  buildItemHierarchy,
  flattenVisibleItemTree,
} from "../utils/subcontract-items/itemHierarchy";
import {
  CONTRACT_ITEM_STATUSES,
  DIFFICULTY_LEVELS,
  PRIORITIES,
  UNITS,
} from "constants/projectRules";
import { subcontractApi } from "../services/subcontractApi";
import { formatInputDate } from "utils/formatters";

const normalize = (value) => String(value || "").trim().toUpperCase();
const getId = (value, key) => value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const draftItemIdOf = (item) => item?.drafItemId ?? item?.DrafItemId;
const getScopeId = (value) => value?.subcontractItemScopeId ?? value?.SubcontractItemScopeId ?? value?.scopeId;

const ApprovalSubmitGroupTable = React.memo(function ApprovalSubmitGroupTable({
  group,
  draftSelectionColumns,
  expandedItemIds,
  onExpandItemRows,
  onCollapseItemRows,
}) {
  const tableData = useMemo(
    () => flattenVisibleItemTree(buildItemHierarchy(group.items || []), expandedItemIds),
    [expandedItemIds, group],
  );

  return <DataTable
    columns={draftSelectionColumns}
    data={tableData}
    onExpandAll={() => onExpandItemRows?.(group.items || [])}
    onCollapseAll={() => onCollapseItemRows?.(group.items || [])}
    initialState={{
      columnVisibility: {
        priority: false,
        status: false
      },
    }}
    minTableWidth={1530} />;
});

const ApprovalSubmitDialog = React.memo(function ApprovalSubmitDialog({
  open,
  onClose,
  title,
  submitLabel,
  initialForm,
  showGeneralFields = true,
  sections,
  items,
  userOptions,
  submitting,
  draftNotice,
  draftSelectionColumns,
  expandedItemIds,
  onExpandItemRows,
  onCollapseItemRows,
  canSubmit,
  approvalSteps,
  groupApprovalItemsByScope,
  onAddDraftItem,
  onSubmit,
}) {
  const [form, setForm] = useState({
    approvalName: "",
    approverUserId: "",
    step1ApproverUserId: "",
    step2ApproverUserId: "",
    step3ApproverUserId: "",
    step4ApproverUserId: "",
    responseDeadline: "",
    overallComment: "",
  });
  const [formError, setFormError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => { setSnackbarOpen(Boolean(formError)); }, [formError]);

  useEffect(() => {
    if (!open) return;
    setForm((current) => ({
      approvalName: initialForm?.approvalName ?? current.approvalName ?? "",
      approverUserId: initialForm?.approverUserId ?? current.approverUserId ?? "",
      step1ApproverUserId: initialForm?.step1ApproverUserId ?? current.step1ApproverUserId ?? "",
      step2ApproverUserId: initialForm?.step2ApproverUserId ?? current.step2ApproverUserId ?? "",
      step3ApproverUserId: initialForm?.step3ApproverUserId ?? current.step3ApproverUserId ?? "",
      step4ApproverUserId: initialForm?.step4ApproverUserId ?? current.step4ApproverUserId ?? "",
      responseDeadline: initialForm?.responseDeadline ?? current.responseDeadline ?? "",
      overallComment: initialForm?.overallComment ?? current.overallComment ?? "",
    }));
    setFormError("");
  }, [initialForm, open]);

  const updateForm = useCallback((name, value) => {
    setFormError("");
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "step1ApproverUserId" ? { approverUserId: value } : {}),
    }));
  }, []);

  const selectedSection = sections.length === 1 ? sections[0] : null;

  const scopeOptions = useMemo(() => {
    const scopes = selectedSection?.scopes || [];
    return scopes
      .filter((scope) => normalize(scope.scopeType) !== "TOTAL")
      .map((scope) => ({
        value: getId(scope, "subcontractItemScopeId"),
        label: scope.scopeName || scope.name || scope.ScopeName,
      }))
      .filter((item) => item.value != null);
  }, [selectedSection]);

  const selectedSectionScopeIds = useMemo(
    () => new Set(scopeOptions.map((scope) => String(scope.value))),
    [scopeOptions],
  );

  const submitItems = useMemo(
    () => items.filter((item) => selectedSectionScopeIds.has(String(getScopeId(item)))),
    [items, selectedSectionScopeIds],
  );

  const submitItemGroups = useMemo(() => {
    const groups = groupApprovalItemsByScope(submitItems);
    scopeOptions.forEach((scope) => {
      if (!groups.some((group) => String(group.scopeId) === String(scope.value))) {
        groups.push({ key: `scope-${scope.value}`, scopeId: scope.value, label: scope.label, items: [] });
      }
    });
    return groups;
  }, [groupApprovalItemsByScope, scopeOptions, submitItems]);

  const submit = async () => {
    if (sections.length === 0) return setFormError("Giao khoán chưa được cấu hình loại dự toán.");
    if (sections.length > 1) return setFormError("Giao khoán có nhiều hơn một loại dự toán. Vui lòng kiểm tra lại dữ liệu cấu hình.");
    if (showGeneralFields && !form.approverUserId) return setFormError("Vui lòng chọn người phê duyệt.");
    if (showGeneralFields && approvalSteps.some((step) => !form[step.field])) return setFormError("Vui lòng chọn đủ cấp phê duyệt.");

    const approverIds = approvalSteps.map((step) => Number(form[step.field]));
    if (showGeneralFields && new Set(approverIds).size !== approverIds.length) {
      return setFormError("Vui lòng chọn 4 người phê duyệt khác nhau.");
    }

    const itemIds = submitItems.map(draftItemIdOf).filter((id) => Number(id) > 0);
    if (itemIds.length !== submitItems.length) {
      return setFormError("Không xác định được ID bản nháp. Vui lòng tải lại dữ liệu dự toán.");
    }
    if (scopeOptions.length === 0) {
      return setFormError("Không có phạm vi dự toán chi tiết để gửi phê duyệt.");
    }

    try {
      await onSubmit?.({ form, submitItems, subcontractItemScopeIds: scopeOptions.map((scope) => Number(scope.value)) });
    } catch (error) {
      setFormError(error?.message || "Không thể gửi hồ sơ phê duyệt.");
    }
  };

  return (
    <>
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="subcontract-dialog"
      PaperProps={{
        sx: {
          width: "calc(100vw - 32px)",
          maxWidth: "none",
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
        },
      }}
      title={title}
      loading={submitting}
      size="fullScreen"
      contentSx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      actions={
        <>
          <AppButton variant="outlined" color="inherit" onClick={onClose} disabled={submitting}>Hủy</AppButton>
          <AppButton variant="contained" onClick={submit} disabled={submitting}>{submitLabel}</AppButton>
        </>
      }
    >
      <Box className="subcontract-stack">
        {formError && <Alert severity="error">{formError}</Alert>}
        {draftNotice && <Alert severity={draftNotice.severity || "info"}>{draftNotice.message}</Alert>}
        <Box
          className="subcontract-form-grid"
          sx={{
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            width: "calc(100vw - 112px)",
          }}
        >
          {showGeneralFields && (
            <>
              <Box
                className="full"
                sx={{
                  display: "grid",
                  gridTemplateColumns: "3fr 1fr",
                  gap: "16px",
                }}
              >
                <AppTextField disabled={submitting} label="Tiêu đề phê duyệt" value={form.approvalName} onChange={(value) => updateForm("approvalName", value)} />
                <AppTextField disabled={submitting} label="Thời hạn phản hồi" type="date" value={form.responseDeadline} onChange={(value) => updateForm("responseDeadline", value)} />
              </Box>
              {approvalSteps.map((step) => (
                <AppAutocomplete
                  disabled={submitting}
                  key={step.field}
                  required
                  label={step.label}
                  value={form[step.field]}
                  options={userOptions}
                  width={300}
                  onChange={(value) => updateForm(step.field, value)}
                />
              ))}
              <AppTextField disabled={submitting} label="Nội dung" multiline minRows={3} className="full" value={form.overallComment} onChange={(value) => updateForm("overallComment", value)} />
            </>
          )}
        </Box>
        <Box>
          <Typography fontWeight={700} sx={{ mb: 1 }}>Chi tiết dự toán gửi phê duyệt</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Sẽ gửi {submitItems.length} dự toán chi tiết. Phạm vi Tổng hợp chỉ dùng để xem, không gửi phê duyệt.
          </Typography>
          {submitItemGroups.length === 0 ? (
            <Alert severity="info">Không có dự toán chi tiết trong loại giao khoán đã chọn.</Alert>
          ) : submitItemGroups.map((group) => (
            <Box key={group.key} sx={{ mt: 2 }}>
              <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                <Box>
                  <Typography fontWeight={750}>{group.label}</Typography>
                  {group.subtitle && (
                    <Typography sx={{ fontSize: "12px", fontStyle: "italic" }} color="text.secondary">
                      {group.subtitle}
                    </Typography>
                  )}
                </Box>
                {canSubmit && (
                  <AppButton disabled={submitting} size="small" startIcon={<Add />} onClick={() => onAddDraftItem?.({ scopeId: group.scopeId })}>
                    Thêm dự toán
                  </AppButton>
                )}
              </Box>
              <ApprovalSubmitGroupTable
                group={group}
                draftSelectionColumns={draftSelectionColumns}
                expandedItemIds={expandedItemIds}
                onExpandItemRows={onExpandItemRows}
                onCollapseItemRows={onCollapseItemRows}
              />
            </Box>
          ))}
        </Box>

      </Box>
    </AppDialog>
    <AppSnackbar
      open={snackbarOpen}
      message={formError}
      severity="error"
      onClose={() => setSnackbarOpen(false)}
    />
    </>
  );
});

export default function ApprovalItemsEditDialogs({
  submitDialogOpen,
  onCloseSubmitDialog,
  approvalDialogTitle,
  approvalActionLabel,
  approvalSubmitInitialForm,
  isEditingCurrentApproval,
  sections,
  items,
  userOptions,
  submitting,
  draftNotice,
  draftSelectionColumns,
  expandedItemIds,
  onExpandItemRows,
  onCollapseItemRows,
  canSubmit,
  approvalSteps,
  groupApprovalItemsByScope,
  onAddDraftItem,
  onSubmitApproval,
  draftDialog,
  onCloseDraftDialog,
  onSaveDraftItem,
  draftParentOptions,
  draftScopeItems = [],
  draftOfficialScopeItems = [],
}) {
  const [unitOptions, setUnitOptions] = useState(UNITS);

  useEffect(() => {
    let active = true;
    subcontractApi.getUnitOptions().then((options) => {
      if (active && options?.length) {
        setUnitOptions(options);
      }
    }).catch(() => { console.warn("Không tải được danh mục đơn vị; đang dùng danh mục mặc định."); });
    return () => { active = false; };
  }, []);

  return (
    <>
      <ApprovalSubmitDialog
        open={submitDialogOpen}
        onClose={onCloseSubmitDialog}
        title={approvalDialogTitle}
        submitLabel={approvalActionLabel}
        initialForm={approvalSubmitInitialForm}
        showGeneralFields={!isEditingCurrentApproval}
        sections={sections}
        items={items}
        userOptions={userOptions}
        submitting={submitting}
        draftNotice={draftNotice}
        draftSelectionColumns={draftSelectionColumns}
        expandedItemIds={expandedItemIds}
        onExpandItemRows={onExpandItemRows}
        onCollapseItemRows={onCollapseItemRows}
        canSubmit={canSubmit}
        approvalSteps={approvalSteps}
        groupApprovalItemsByScope={groupApprovalItemsByScope}
        onAddDraftItem={onAddDraftItem}
        onSubmit={onSubmitApproval}
      />

      <EntityFormDialog
        open={Boolean(draftDialog)}
        onClose={onCloseDraftDialog}
        title={draftDialog?.data ? "Cập nhật dự toán" : "Thêm dự toán"}
        initialData={draftDialog?.data
          ? {
            ...draftDialog.data,
            drafParentItemId: draftDialog.data.drafParentItemId ?? draftDialog.data.DrafParentItemId ?? "",
            startDate: formatInputDate(draftDialog.data.startDate ?? draftDialog.data.StartDate),
            endDate: formatInputDate(draftDialog.data.endDate ?? draftDialog.data.EndDate),
          }
          : { status: "Chưa bắt đầu", completionPercentage: 0, subcontractQuantity: 0, laborCount: null, laborNorm: null, laborPrice: null, priority: 2, difficultyLevel: 1 }}
        transformForm={(form) => draftDialog?.data ? form : {
          ...form, itemCode: getDraftParentError(form.drafParentItemId, draftScopeItems) ? "" :
            getNextItemCode(draftScopeItems, draftScopeItems.find((item) =>
              String(draftItemIdOf(item)) === String(form.drafParentItemId)), draftOfficialScopeItems),
        }}
        onSubmit={onSaveDraftItem}
        topContent={(form) => <>
          <Box mb={2} p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
            <Typography variant="body2">{draftDialog?.data ? "STT: " : "STT sẽ được tạo: "}<strong>{(form.itemCode ?? form.ItemCode) || "—"}</strong></Typography>
          </Box>
          {!draftDialog?.data && getDraftParentError(form.drafParentItemId, draftScopeItems) &&
            <Alert severity="error" sx={{ mb: 2 }}>{getDraftParentError(form.drafParentItemId, draftScopeItems)}</Alert>}
        </>}
        fields={[
          { name: "drafParentItemId", label: "Dự toán cha", full: true, options: draftParentOptions, disabled: Boolean(draftDialog?.data),
            validate: (value) => draftDialog?.data ? "" : getDraftParentError(value, draftScopeItems) },
          { name: "itemName", label: "Dự toán", full: true, required: true },
          { name: "unit", label: "Đơn vị tính", options: unitOptions },
          { name: "difficultyLevel", label: "Mức KK", options: DIFFICULTY_LEVELS },
          { name: "subcontractQuantity", label: "Khối lượng", type: "number", inputProps: { min: 0, step: "any" } },
          { name: "laborCount", label: "Định biên", type: "number", inputProps: { min: 0, step: "any" } },
          { name: "laborNorm", label: "Định mức", type: "number", inputProps: { min: 0, step: "any" } },
          { name: "laborPrice", label: "Đơn giá công", type: "number", inputProps: { min: 0, step: "any" } },
          { name: "priority", label: "Độ ưu tiên", options: PRIORITIES },
          { name: "status", label: "Trạng thái", options: CONTRACT_ITEM_STATUSES },
          { name: "startDate", label: "Từ ngày", type: "date" },
          {
            name: "endDate",
            label: "Đến ngày",
            type: "date",
            validate: (value, form) => value && form.startDate && value < form.startDate
              ? "Đến ngày phải sau hoặc bằng Từ ngày."
              : "",
          },
          {
            name: "unitPrice",
            label: "Đơn giá sản phẩm",
            type: "number",
            disabled: true,
            helperText: "Hệ thống tự tính: Định biên × Định mức × Đơn giá công.",
            inputProps: { min: 0, step: "any" },
          },
          { name: "completionPercentage", label: "Tiến độ (%)", type: "number", inputProps: { min: 0, max: 100 }, disabled: true },
          { name: "notes", label: "Ghi chú", multiline: true, full: true },
        ]}
      />
    </>
  );
}
